"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import axios from "axios";
import {
  TbAlertCircle,
  TbBuilding,
  TbCalendar,
  TbCar,
  TbChevronDown,
  TbChevronUp,
  TbCircleCheck,
  TbCheck,
  TbEdit,
  TbEye,
  TbExternalLink,
  TbFileText,
  TbFilter,
  TbInfoCircle,
  TbListDetails,
  TbNotes,
  TbPlus,
  TbRefresh,
  TbRoute,
  TbPhoto,
  TbSearch,
  TbShield,
  TbTrash,
  TbTruck,
  TbUser,
  TbX,
} from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || "https://new.cajuu.app";

const EMPTY_FILTERS = {
  q: "",
  order_id: "",
  status: "all",
  company_id: "all",
  company_q: "",
  shipper_q: "",
  customer_q: "",
  dispatcher_q: "",
  loadboard: "all",
  origin_q: "",
  destination_q: "",
  payment_rate_min: "",
  payment_rate_max: "",
  payment_type: "all",
  payment_method: "all",
  created_from: "",
  created_to: "",
  expected_pickup_from: "",
  expected_pickup_to: "",
  pickup_from: "",
  pickup_to: "",
  expected_delivery_from: "",
  expected_delivery_to: "",
  delivery_from: "",
  delivery_to: "",
};

const LOAD_FIELDS = [
  "driver_id",
  "partner_company_id",
  "shipper_id",
  "dispatcher_id",
  "customer_id",
  "load_status_id",
  "quote_id",
  "loadboard",
  "order_id",
  "pickup_address",
  "pickup_city",
  "pickup_state",
  "pickup_zipcode",
  "pickup_business_name",
  "pickup_location_type",
  "pickup_customer_name",
  "pickup_customer_phone",
  "pickup_customer_email",
  "pickup_notes",
  "delivery_address",
  "delivery_city",
  "delivery_state",
  "delivery_zipcode",
  "delivery_business_name",
  "delivery_location_type",
  "delivery_customer_name",
  "delivery_customer_phone",
  "delivery_customer_email",
  "delivery_notes",
  "payment_rate",
  "payment_type",
  "payment_method",
  "broker_fee",
  "storage_fee",
  "dispatch_date",
  "expected_pickup_date",
  "pickup_date",
  "expected_dropoff_date",
  "dropoff_date",
  "distance_route_miles",
  "driving_time_hours",
  "billed",
  "paid",
  "dispatch_sheet_pdf_path",
  "invoice_pdf_path",
  "confirmation_notes",
];

const VEHICLE_FIELDS = [
  "car_type_id",
  "make",
  "model",
  "year",
  "vin",
  "is_operable",
  "length",
  "weight",
  "buyer_number",
  "lot_number",
  "additional_info",
  "pickup_bol_path",
  "dropoff_bol_path",
  "profile_picture_path",
];

const INSPECTION_FIELDS = [
  "inspection_type",
  "odometer",
  "customer_name",
  "customer_phone",
  "customer_email",
  "notes",
  "diagram_path",
  "remote_signature_token",
  "customer_signature_path",
  "drivable",
  "windscreen",
  "intact_glass",
  "paperwork",
  "keys_qt",
  "spare_tire",
  "others",
];

const STORAGE_PREFIX = "https://new.cajuu.app/storage/";

const LOADBOARD_OPTIONS = [
  "Cajuu",
  "Central dispatch",
  "Ship.cars",
  "Autosled",
  "Acertus",
  "Runbugy",
  "Ready",
  "Carsarrive",
  "United",
  "ACV Transportation",
  "Uship",
  "Email",
];

const PAYMENT_TYPE_OPTIONS = ["COD", "COP", "2 BD", "5 BD", "7 BD", "15 BD", "30 BD"];

const PAYMENT_METHOD_OPTIONS = [
  "ACH",
  "Check",
  "Zelle",
  "Venmo",
  "CashApp",
  "Uship",
  "Cash",
  "PayPal",
  "Credit card",
  "Debit",
  "Comcheck",
  "Echeck",
  "Electronic Check",
];

const LOCATION_TYPE_OPTIONS = [
  "Airport",
  "Auction",
  "Business",
  "Dealership",
  "Parking",
  "Port",
  "Repo",
  "Residence",
  "Terminal",
];

function Snackbar({ message, type = "success", onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: "bg-green-600 border-green-700",
    error: "bg-red-600 border-red-700",
    info: "bg-blue-600 border-blue-700",
    warning: "bg-amber-600 border-amber-700",
  }[type];

  const icon = {
    success: <TbCircleCheck className="w-5 h-5" />,
    error: <TbAlertCircle className="w-5 h-5" />,
    info: <TbInfoCircle className="w-5 h-5" />,
    warning: <TbInfoCircle className="w-5 h-5" />,
  }[type];

  return (
    <div
      className={`fixed bottom-4 right-4 z-[90] transform transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div
        className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg border max-w-sm flex items-start gap-3`}
      >
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-white/80 hover:text-white ml-2"
          type="button"
        >
          <TbX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function Modal({ open, title, subtitle, icon, onClose, children, footer, maxWidth = "max-w-6xl" }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6">
      <div className={`w-full ${maxWidth} max-h-[92vh] bg-[#1f1e1c] border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col`}>
        <div className="bg-[#181715] border-b border-zinc-700 px-5 py-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-300">
              {icon || <TbListDetails className="w-5 h-5" />}
            </div>
            <div>
              <h2 className="text-white text-lg font-semibold">{title}</h2>
              {subtitle && <p className="text-white/50 text-sm mt-1">{subtitle}</p>}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors"
          >
            <TbX className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">{children}</div>

        {footer && <div className="bg-[#181715] border-t border-zinc-700 px-5 py-4">{footer}</div>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", disabled = false, step, min, max, inputMode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-white/60 mb-1.5">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        step={step}
        min={min}
        max={max}
        inputMode={inputMode}
        style={type === "date" || type === "datetime-local" ? { colorScheme: "dark" } : undefined}
        className="w-full bg-[#141311] border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/50 disabled:opacity-60"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, placeholder = "" }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-white/60 mb-1.5">{label}</span>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full bg-[#141311] border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/50"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-white/60 mb-1.5">{label}</span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#141311] border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/50"
      >
        {children}
      </select>
    </label>
  );
}


function FilterTextInput({ value, onChange, placeholder, icon, type = "text", onClear }) {
  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
        {icon || <TbSearch className="text-white text-lg" />}
      </div>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
      />
      {Boolean(value) && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
        >
          <TbX className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, children, icon, ariaLabel = "Filter select" }) {
  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
        {icon || <TbFilter className="text-amber-500 text-lg" />}
      </div>
      <select
        aria-label={ariaLabel}
        value={value ?? "all"}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
      >
        {children}
      </select>
      <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
        <TbChevronDown className="text-white text-lg" />
      </div>
    </div>
  );
}

function DateFilter({ label, value, onChange }) {
  return (
    <div className="relative">
      <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400 z-[1]">{label}</span>
      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
        <TbCalendar className="text-white text-lg" />
      </div>
      <input
        type="date"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => e.target.showPicker?.()}
        style={{ colorScheme: "dark" }}
        className="w-full pl-10 pr-10 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
      />
    </div>
  );
}

function HybridSearchDropdown({
  selectedValue = "all",
  searchValue = "",
  options = [],
  allLabel = "All",
  placeholder = "Search...",
  onSelect,
  onSearchChange,
  icon,
}) {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const normalizedSelectedValue = selectedValue === null || selectedValue === undefined || selectedValue === "" ? "all" : String(selectedValue);

  const selectedOption = useMemo(() => {
    if (normalizedSelectedValue === "all") return null;
    return options.find((option) => String(option.value) === normalizedSelectedValue) || null;
  }, [normalizedSelectedValue, options]);

  useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.label || "");
      return;
    }

    setInputValue(searchValue || "");
  }, [selectedOption, searchValue]);

  const visibleOptions = useMemo(() => {
    const query = String(inputValue || "").trim().toLowerCase();

    if (!query) {
      return options.slice(0, 80);
    }

    return options
      .filter((option) => {
        const label = String(option.label || "").toLowerCase();
        const description = String(option.description || "").toLowerCase();
        return label.includes(query) || description.includes(query);
      })
      .slice(0, 80);
  }, [inputValue, options, selectedOption]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    setOpen(true);
    onSearchChange?.(value);
  };

  const handleSelectAll = () => {
    setInputValue("");
    setOpen(false);
    onSelect?.("all", null);
    onSearchChange?.("");
  };

  const handleSelectOption = (option) => {
    setInputValue(option.label || "");
    setOpen(false);
    onSelect?.(String(option.value), option);
  };

  const handleClear = () => {
    setInputValue("");
    setOpen(false);
    onSelect?.("all", null);
    onSearchChange?.("");
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          {icon || <TbSearch className="text-white text-lg" />}
        </div>
        <input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
        />
        {(inputValue || normalizedSelectedValue !== "all") && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
          >
            <TbX className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-20 mt-2 w-full bg-[#1c1b19] border border-zinc-600 rounded-lg overflow-hidden shadow-lg">
          <div className="max-h-56 overflow-y-auto">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSelectAll}
              className="w-full px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600 text-left"
            >
              <span className="px-2 py-1 text-white text-sm">{allLabel}</span>
              {normalizedSelectedValue === "all" && !inputValue && <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />}
            </button>

            {visibleOptions.length > 0 ? (
              <>
                {visibleOptions.map((option) => (
                  <button
                    type="button"
                    key={`${option.value}-${option.label}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelectOption(option)}
                    className="w-full px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600 last:border-b-0 text-left"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block px-2 text-white text-sm truncate" title={option.label}>{option.label}</span>
                      {option.description && <span className="block px-2 mt-0.5 text-xs text-white/40 truncate" title={option.description}>{option.description}</span>}
                    </span>
                    {normalizedSelectedValue === String(option.value) && <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />}
                  </button>
                ))}
                <div className="px-4 py-2 text-xs text-gray-400 border-t border-zinc-600 bg-zinc-900/50">
                  {inputValue && !selectedOption ? `Found ${visibleOptions.length} option(s)` : `Showing ${visibleOptions.length} option(s)`}
                </div>
              </>
            ) : (
              <div className="px-4 py-3 text-center text-gray-400 text-sm">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SearchSelectField({ label, value, onChange, options = [], placeholder = "Search and select...", disabled = false }) {
  const selectedValue = value === null || value === undefined || value === "" ? "all" : String(value);

  return (
    <label className="block">
      <span className="block text-xs font-medium text-white/60 mb-1.5">{label}</span>
      {disabled ? (
        <input
          type="text"
          disabled
          value={placeholder || "—"}
          className="w-full bg-[#141311] border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-70"
        />
      ) : (
        <HybridSearchDropdown
          selectedValue={selectedValue}
          searchValue=""
          options={options}
          allLabel="—"
          placeholder={placeholder}
          icon={<TbSearch className="text-white text-lg" />}
          onSelect={(selected) => onChange(selected === "all" ? "" : selected)}
          onSearchChange={() => {}}
        />
      )}
    </label>
  );
}

function DateTimeField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-white/60 mb-1.5">{label}</span>
      <div className="relative">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <TbCalendar className="text-white text-lg" />
        </div>
        <input
          type="datetime-local"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => e.target.showPicker?.()}
          style={{ colorScheme: "dark" }}
          className="w-full bg-[#141311] border border-zinc-700 rounded-lg pl-10 pr-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/50"
        />
      </div>
    </label>
  );
}

function StaticFileField({ label, value }) {
  const cleanValue = value ?? "";
  const href = cleanValue ? `${STORAGE_PREFIX}${cleanValue}` : "";

  return (
    <div className="block">
      <span className="block text-xs font-medium text-white/60 mb-1.5">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={cleanValue || "—"}
          disabled
          className="min-w-0 flex-1 bg-[#141311] border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white/70 disabled:opacity-70"
        />
        {href && (
          <button
            type="button"
            onClick={() => window.open(href, "_blank", "noopener,noreferrer")}
            title={`Open ${label}`}
            className="p-2 text-gray-400 hover:text-amber-500 hover:bg-zinc-700/50 rounded-lg transition-colors border border-zinc-700"
          >
            <TbExternalLink className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function ImageFileField({ label, value }) {
  const cleanValue = value ?? "";
  const href = cleanValue ? `${STORAGE_PREFIX}${cleanValue}` : "";

  return (
    <div className="block">
      <span className="block text-xs font-medium text-white/60 mb-1.5">{label}</span>
      <div className="flex items-center gap-3">
        {href ? (
          <button
            type="button"
            onClick={() => window.open(href, "_blank", "noopener,noreferrer")}
            title={`Open ${label}`}
            className="h-12 w-16 rounded-lg overflow-hidden border border-zinc-700 bg-zinc-900 hover:border-amber-500 transition-colors flex items-center justify-center"
          >
            <img src={href} alt={label} className="h-full w-full object-cover" />
          </button>
        ) : (
          <div className="h-12 w-16 rounded-lg border border-zinc-700 bg-zinc-900 flex items-center justify-center text-white/30">
            <TbPhoto className="w-5 h-5" />
          </div>
        )}
        <input
          type="text"
          value={cleanValue || "—"}
          disabled
          className="min-w-0 flex-1 bg-[#141311] border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white/70 disabled:opacity-70"
        />
      </div>
    </div>
  );
}

function IconOnlyButton({ title, onClick, children, tone = "default", disabled = false }) {
  const toneClass = {
    default: "text-gray-400 hover:text-amber-500 hover:bg-zinc-700/50",
    danger: "text-gray-400 hover:text-red-500 hover:bg-red-500/20",
    info: "text-gray-400 hover:text-blue-400 hover:bg-blue-500/10",
    success: "text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10",
  }[tone] || "text-gray-400 hover:text-amber-500 hover:bg-zinc-700/50";

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${toneClass}`}
    >
      {children}
    </button>
  );
}

function ToggleField({ label, value, onChange }) {
  return (
    <SelectField label={label} value={value === true || value === 1 || value === "1" ? "1" : value === false || value === 0 || value === "0" ? "0" : ""} onChange={(v) => onChange(v === "" ? "" : v === "1")}>
      <option value="">—</option>
      <option value="1">Yes</option>
      <option value="0">No</option>
    </SelectField>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-black/20 border border-zinc-700 rounded-xl p-4">
      <div className="flex items-center gap-2 text-white font-semibold mb-4">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function ActionButton({ children, onClick, variant = "secondary", disabled = false, type = "button" }) {
  const classes = {
    primary: "bg-amber-500 text-black hover:bg-amber-400 border-amber-400",
    secondary: "bg-zinc-800 text-white hover:bg-zinc-700 border-zinc-700",
    danger: "bg-red-600/90 text-white hover:bg-red-600 border-red-500",
    ghost: "bg-transparent text-white hover:bg-zinc-800 border-zinc-700",
  }[variant];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${classes}`}
    >
      {children}
    </button>
  );
}

function SortButton({ label, column, sortBy, sortDirection, onSort }) {
  const active = sortBy === column;

  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={`inline-flex items-center gap-1 hover:text-white transition-colors ${active ? "text-amber-300" : "text-white/70"}`}
    >
      {String(label).toUpperCase()}
      {active ? (
        sortDirection === "asc" ? <TbChevronUp className="w-4 h-4" /> : <TbChevronDown className="w-4 h-4" />
      ) : null}
    </button>
  );
}

function LoadingOverlay({ show }) {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-[1px] z-[100] flex items-center justify-center">
      <div className="px-4 py-3 rounded-xl bg-[#1f1e1c] border border-zinc-700 text-white text-sm flex items-center gap-2 shadow-2xl">
        <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
        Loading...
      </div>
    </div>
  );
}

function valueOrDash(value) {
  if (value === null || value === undefined || value === "") return "—";
  return value;
}

function money(value) {
  const n = Number(value || 0);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function formatDateTime(value) {
  if (!value) return "—";
  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "—";

  return `${date.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" })} ${date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
}

function dateInputValue(value) {
  if (!value) return "";
  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10);
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}

function dateTimeLocalValue(value) {
  if (!value) return "";
  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 16);
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function apiDateTime(value) {
  if (!value) return "";
  return String(value).replace("T", " ");
}

function userName(user) {
  if (!user) return "—";
  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  return fullName || user.username || user.email || `User #${user.id}`;
}

function driverName(driver) {
  if (!driver) return "—";
  return userName(driver.user) || `Driver #${driver.id}`;
}

function vehicleLabel(vehicle) {
  const label = [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(" ");
  return label || `Vehicle #${vehicle?.id || ""}`;
}

function vehicleLabelUpper(vehicle) {
  return vehicleLabel(vehicle).toUpperCase();
}

function locationLine(load, prefix) {
  const business = load?.[`${prefix}_business_name`];
  const address = load?.[`${prefix}_address`];
  const cityStateZip = [load?.[`${prefix}_city`], load?.[`${prefix}_state`], load?.[`${prefix}_zipcode`]].filter(Boolean).join(", ");
  return [business, address, cityStateZip].filter(Boolean).join(" • ");
}

function getActivityLogs(load) {
  return load?.activity_logs || load?.activityLogs || [];
}

function getAuditTimeline(load) {
  const notes = (load?.notes || []).map((note) => ({
    id: `note-${note.id}`,
    type: "Note",
    title: `Note #${note.id}`,
    description: note.message || "—",
    date: note.created_at,
    author: note.author ? userName(note.author) : "—",
  }));

  const logs = getActivityLogs(load).map((log) => ({
    id: `log-${log.id}`,
    type: "Log",
    title: log.event_type || `Log #${log.id}`,
    description: log.description || "—",
    date: log.happened_at || log.created_at,
    author: log.user ? userName(log.user) : "System",
  }));

  return [...notes, ...logs].sort((a, b) => new Date(String(b.date || "").replace(" ", "T")) - new Date(String(a.date || "").replace(" ", "T")));
}

function optionLabelByValue(options, value) {
  if (value === null || value === undefined || value === "") return "—";
  const option = (options || []).find((item) => String(item.value) === String(value));
  return option?.label || "—";
}

function extractData(response) {
  return response?.data?.data || null;
}

function buildLoadForm(load) {
  const form = {};

  LOAD_FIELDS.forEach((field) => {
    if (["dispatch_date", "expected_pickup_date", "pickup_date", "expected_dropoff_date", "dropoff_date"].includes(field)) {
      form[field] = dateTimeLocalValue(load?.[field]);
    } else if (["billed", "paid"].includes(field)) {
      form[field] = load?.[field] === true || load?.[field] === 1 ? "1" : load?.[field] === false || load?.[field] === 0 ? "0" : "";
    } else {
      form[field] = load?.[field] ?? "";
    }
  });

  return form;
}

function normalizeLoadPayload(form) {
  const payload = {};

  LOAD_FIELDS.forEach((field) => {
    const value = form[field];

    if (["dispatch_date", "expected_pickup_date", "pickup_date", "expected_dropoff_date", "dropoff_date"].includes(field)) {
      payload[field] = value ? apiDateTime(value) : "";
      return;
    }

    if (["billed", "paid"].includes(field)) {
      payload[field] = value === "" ? "" : value === "1" || value === true;
      return;
    }

    payload[field] = value ?? "";
  });

  return payload;
}

function buildVehicleForm(vehicle = {}) {
  const form = {};
  VEHICLE_FIELDS.forEach((field) => {
    if (field === "is_operable") {
      form[field] = vehicle?.[field] === true || vehicle?.[field] === 1 ? "1" : vehicle?.[field] === false || vehicle?.[field] === 0 ? "0" : "";
    } else {
      form[field] = vehicle?.[field] ?? "";
    }
  });

  return form;
}

function normalizeVehiclePayload(form) {
  const payload = {};
  VEHICLE_FIELDS.forEach((field) => {
    if (field === "is_operable") {
      payload[field] = form[field] === "" ? "" : form[field] === "1" || form[field] === true;
    } else {
      payload[field] = form[field] ?? "";
    }
  });

  return payload;
}

function buildInspectionForm(inspection = {}) {
  const form = {};
  INSPECTION_FIELDS.forEach((field) => {
    if (["drivable", "windscreen", "intact_glass", "paperwork", "spare_tire"].includes(field)) {
      form[field] = inspection?.[field] === true || inspection?.[field] === 1 ? "1" : inspection?.[field] === false || inspection?.[field] === 0 ? "0" : "";
    } else {
      form[field] = inspection?.[field] ?? "";
    }
  });

  return form;
}

function normalizeInspectionPayload(form) {
  const payload = {};
  INSPECTION_FIELDS.forEach((field) => {
    if (["drivable", "windscreen", "intact_glass", "paperwork", "spare_tire"].includes(field)) {
      payload[field] = form[field] === "" ? "" : form[field] === "1" || form[field] === true;
    } else {
      payload[field] = form[field] ?? "";
    }
  });

  return payload;
}

function LoadEditModal({
  open,
  load,
  form,
  setForm,
  filterOptions,
  tenantOptions,
  onClose,
  onSave,
  saving,
}) {
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const driverOptions = useMemo(() => {
    return (tenantOptions.drivers || []).map((driver) => ({
      value: String(driver.id),
      label: driver.name || `Driver #${driver.id}`,
      description: [driver.email || "", driver.phone || "", driver.truck || ""].filter(Boolean).join(" • "),
    }));
  }, [tenantOptions.drivers]);

  const dispatcherOptions = useMemo(() => {
    return (tenantOptions.dispatchers || []).map((dispatcher) => ({
      value: String(dispatcher.id),
      label: dispatcher.name || userName(dispatcher),
      description: [dispatcher.email || "", dispatcher.phone || ""].filter(Boolean).join(" • "),
    }));
  }, [tenantOptions.dispatchers]);

  const customerOptions = useMemo(() => {
    return (tenantOptions.customers || []).map((customer) => ({
      value: String(customer.id),
      label: customer.name || userName(customer),
      description: [customer.email || "", customer.phone || ""].filter(Boolean).join(" • "),
    }));
  }, [tenantOptions.customers]);

  const shipperOptions = useMemo(() => {
    return (tenantOptions.shippers || filterOptions.shippers || []).map((shipper) => ({
      value: String(shipper.id),
      label: shipper.shipper_name || `Shipper #${shipper.id}`,
    }));
  }, [tenantOptions.shippers, filterOptions.shippers]);

  const companyName = load?.partner_company?.partner_company_name || load?.partnerCompany?.partner_company_name || "—";

  return (
    <Modal
      open={open}
      title="Edit LoadTransport"
      subtitle={load ? `Order ${valueOrDash(load.order_id)} / ${load.id} • tenant-safe SuperAdmin edition` : ""}
      icon={<TbEdit className="w-5 h-5" />}
      onClose={onClose}
      maxWidth="max-w-7xl"
      footer={
        <div className="flex justify-end gap-3">
          <ActionButton onClick={onClose} variant="ghost">Cancel</ActionButton>
          <ActionButton onClick={onSave} variant="primary" disabled={saving}>
            {saving ? "Saving..." : "Save LoadTransport"}
          </ActionButton>
        </div>
      }
    >
      <div className="space-y-4">
        <Section title="Main Information" icon={<TbTruck className="w-4 h-4 text-amber-300" />}>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4">
            <Field label="Order ID" value={form.order_id} onChange={(v) => update("order_id", v)} />
            <SelectField label="Status" value={form.load_status_id} onChange={(v) => update("load_status_id", v)}>
              <option value="">—</option>
              {(filterOptions.loadStatuses || []).map((status) => (
                <option key={status.id} value={status.id}>{status.load_status_name}</option>
              ))}
            </SelectField>
            <Field label="Company / Tenant Context" value={companyName} onChange={() => {}} disabled />
            <SearchSelectField label="Shipper" value={form.shipper_id} onChange={(v) => update("shipper_id", v)} options={shipperOptions} placeholder="Select Shipper" />
            <SearchSelectField label="Driver" value={form.driver_id} onChange={(v) => update("driver_id", v)} options={driverOptions} placeholder="Select Driver" />
            <SearchSelectField label="Dispatcher" value={form.dispatcher_id} onChange={(v) => update("dispatcher_id", v)} options={dispatcherOptions} placeholder="Select Dispatcher" />
            <SearchSelectField label="Customer" value={form.customer_id} onChange={(v) => update("customer_id", v)} options={customerOptions} placeholder="Select Customer" />
            <Field label="Quote ID" type="number" inputMode="numeric" value={form.quote_id} onChange={(v) => update("quote_id", v)} />
            <SelectField label="LoadBoard" value={form.loadboard} onChange={(v) => update("loadboard", v)}>
              <option value="">—</option>
              {LOADBOARD_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </SelectField>
            <ToggleField label="Billed" value={form.billed} onChange={(v) => update("billed", v === "" ? "" : v ? "1" : "0")} />
            <ToggleField label="Paid" value={form.paid} onChange={(v) => update("paid", v === "" ? "" : v ? "1" : "0")} />
          </div>
        </Section>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Section title="Pickup" icon={<TbRoute className="w-4 h-4 text-blue-300" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Address" value={form.pickup_address} onChange={(v) => update("pickup_address", v)} />
              <Field label="City" value={form.pickup_city} onChange={(v) => update("pickup_city", v)} />
              <Field label="State" value={form.pickup_state} onChange={(v) => update("pickup_state", v)} />
              <Field label="Zipcode" value={form.pickup_zipcode} onChange={(v) => update("pickup_zipcode", v)} />
              <Field label="Business Name" value={form.pickup_business_name} onChange={(v) => update("pickup_business_name", v)} />
              <SelectField label="Pickup Location Type" value={form.pickup_location_type} onChange={(v) => update("pickup_location_type", v)}>
                <option value="">—</option>
                {LOCATION_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
              </SelectField>
              <Field label="Customer Name" value={form.pickup_customer_name} onChange={(v) => update("pickup_customer_name", v)} />
              <Field label="Customer Phone" value={form.pickup_customer_phone} onChange={(v) => update("pickup_customer_phone", v)} />
              <Field label="Customer Email" value={form.pickup_customer_email} onChange={(v) => update("pickup_customer_email", v)} />
              <div className="md:col-span-2">
                <TextArea label="Pickup Notes" value={form.pickup_notes} onChange={(v) => update("pickup_notes", v)} />
              </div>
            </div>
          </Section>

          <Section title="Delivery" icon={<TbRoute className="w-4 h-4 text-emerald-300" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Address" value={form.delivery_address} onChange={(v) => update("delivery_address", v)} />
              <Field label="City" value={form.delivery_city} onChange={(v) => update("delivery_city", v)} />
              <Field label="State" value={form.delivery_state} onChange={(v) => update("delivery_state", v)} />
              <Field label="Zipcode" value={form.delivery_zipcode} onChange={(v) => update("delivery_zipcode", v)} />
              <Field label="Business Name" value={form.delivery_business_name} onChange={(v) => update("delivery_business_name", v)} />
              <SelectField label="Delivery Location Type" value={form.delivery_location_type} onChange={(v) => update("delivery_location_type", v)}>
                <option value="">—</option>
                {LOCATION_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
              </SelectField>
              <Field label="Customer Name" value={form.delivery_customer_name} onChange={(v) => update("delivery_customer_name", v)} />
              <Field label="Customer Phone" value={form.delivery_customer_phone} onChange={(v) => update("delivery_customer_phone", v)} />
              <Field label="Customer Email" value={form.delivery_customer_email} onChange={(v) => update("delivery_customer_email", v)} />
              <div className="md:col-span-2">
                <TextArea label="Delivery Notes" value={form.delivery_notes} onChange={(v) => update("delivery_notes", v)} />
              </div>
            </div>
          </Section>
        </div>

        <Section title="Payment, Dates and Route Metrics" icon={<TbCalendar className="w-4 h-4 text-purple-300" />}>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4">
            <Field label="Payment Rate" type="number" step="0.01" inputMode="decimal" value={form.payment_rate} onChange={(v) => update("payment_rate", v)} />
            <SelectField label="Payment Type" value={form.payment_type} onChange={(v) => update("payment_type", v)}>
              <option value="">—</option>
              {PAYMENT_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </SelectField>
            <SelectField label="Payment Method" value={form.payment_method} onChange={(v) => update("payment_method", v)}>
              <option value="">—</option>
              {PAYMENT_METHOD_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </SelectField>
            <Field label="Broker Fee" type="number" step="0.01" inputMode="decimal" value={form.broker_fee} onChange={(v) => update("broker_fee", v)} />
            <Field label="Storage Fee" type="number" step="0.01" inputMode="decimal" value={form.storage_fee} onChange={(v) => update("storage_fee", v)} />
            <DateTimeField label="Dispatch Date" value={form.dispatch_date} onChange={(v) => update("dispatch_date", v)} />
            <DateTimeField label="Expected Pickup" value={form.expected_pickup_date} onChange={(v) => update("expected_pickup_date", v)} />
            <DateTimeField label="Pickup Date" value={form.pickup_date} onChange={(v) => update("pickup_date", v)} />
            <DateTimeField label="Expected Delivery" value={form.expected_dropoff_date} onChange={(v) => update("expected_dropoff_date", v)} />
            <DateTimeField label="Delivery Date" value={form.dropoff_date} onChange={(v) => update("dropoff_date", v)} />
            <Field label="Distance Route Miles" type="number" step="1" inputMode="numeric" value={form.distance_route_miles} onChange={(v) => update("distance_route_miles", v)} />
            <Field label="Driving Time Hours" type="number" step="0.01" inputMode="decimal" value={form.driving_time_hours} onChange={(v) => update("driving_time_hours", v)} />
          </div>
        </Section>

        <Section title="Documents and Notes" icon={<TbFileText className="w-4 h-4 text-amber-300" />}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <StaticFileField label="Dispatch Sheet" value={form.dispatch_sheet_pdf_path} />
            <StaticFileField label="Invoice PDF" value={form.invoice_pdf_path} />
            <div className="lg:col-span-2">
              <TextArea label="Confirmation Notes" value={form.confirmation_notes} onChange={(v) => update("confirmation_notes", v)} />
            </div>
          </div>
        </Section>
      </div>
    </Modal>
  );
}

function VehiclesModal({
  open,
  load,
  carTypes,
  vehicleDrafts,
  setVehicleDrafts,
  newVehicle,
  setNewVehicle,
  onClose,
  onSaveVehicle,
  onDeleteVehicle,
  onCreateVehicle,
  savingId,
}) {
  const updateVehicle = (vehicleId, field, value) => {
    setVehicleDrafts((prev) => ({
      ...prev,
      [vehicleId]: {
        ...(prev[vehicleId] || {}),
        [field]: value,
      },
    }));
  };

  const updateNewVehicle = (field, value) => {
    setNewVehicle((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const vehicleFieldsGrid = (draft, update, isNew = false) => (
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4">
      <SelectField label="Car Type" value={draft.car_type_id} onChange={(v) => update("car_type_id", v)}>
        <option value="">—</option>
        {(carTypes || []).map((type) => (
          <option key={type.id} value={type.id}>{type.type || type.car_type_name || type.name || `Type #${type.id}`}</option>
        ))}
      </SelectField>
      <Field label="Year" type="number" step="1" inputMode="numeric" value={draft.year} onChange={(v) => update("year", v)} />
      <Field label="Make" value={draft.make} onChange={(v) => update("make", v)} />
      <Field label="Model" value={draft.model} onChange={(v) => update("model", v)} />
      <Field label="VIN" value={draft.vin} onChange={(v) => update("vin", v)} />
      <SelectField label="Is It Operable?" value={draft.is_operable} onChange={(v) => update("is_operable", v)}>
        <option value="">—</option>
        <option value="1">Yes</option>
        <option value="0">No</option>
      </SelectField>
      <Field label="Length" type="number" step="0.01" inputMode="decimal" value={draft.length} onChange={(v) => update("length", v)} />
      <Field label="Weight" type="number" step="0.01" inputMode="decimal" value={draft.weight} onChange={(v) => update("weight", v)} />
      <Field label="Buyer Number" value={draft.buyer_number} onChange={(v) => update("buyer_number", v)} />
      <Field label="Lot Number" value={draft.lot_number} onChange={(v) => update("lot_number", v)} />
      {!isNew && <StaticFileField label="Pickup BOL Path" value={draft.pickup_bol_path} />}
      {!isNew && <StaticFileField label="Dropoff BOL Path" value={draft.dropoff_bol_path} />}
      {!isNew && <div className="md:col-span-3 xl:col-span-4"><ImageFileField label="Profile Picture" value={draft.profile_picture_path} /></div>}
      <div className="md:col-span-3 xl:col-span-4">
        <TextArea label="Additional Info" value={draft.additional_info} onChange={(v) => update("additional_info", v)} />
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      title="Vehicles"
      subtitle={load ? `Order ${valueOrDash(load.order_id)} / ${load.id}` : ""}
      icon={<TbCar className="w-5 h-5" />}
      onClose={onClose}
      maxWidth="max-w-7xl"
    >
      <div className="space-y-5">
        <Section title="Add Vehicle" icon={<TbPlus className="w-4 h-4 text-emerald-300" />}>
          <div className="space-y-4">
            {vehicleFieldsGrid(newVehicle, updateNewVehicle, true)}
            <div className="flex justify-end">
              <ActionButton onClick={onCreateVehicle} variant="primary" disabled={savingId === "new-vehicle"}>
                {savingId === "new-vehicle" ? "Adding..." : "Add Vehicle"}
              </ActionButton>
            </div>
          </div>
        </Section>

        {(load?.load_items || load?.loadItems || []).length === 0 && (
          <div className="text-white/60 text-sm border border-dashed border-zinc-700 rounded-xl p-6 text-center">
            No vehicles found for this LoadTransport.
          </div>
        )}

        {(load?.load_items || load?.loadItems || []).map((vehicle) => {
          const draft = vehicleDrafts[vehicle.id] || buildVehicleForm(vehicle);
          const inspections = vehicle.inspection_load_items || vehicle.inspectionLoadItems || [];
          const photos = vehicle.inspection_photos || vehicle.inspectionPhotos || [];

          return (
            <div key={vehicle.id} className="bg-black/20 border border-zinc-700 rounded-xl p-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
                <div>
                  <div className="text-white font-semibold uppercase">{vehicleLabelUpper(vehicle)}</div>
                  <div className="text-xs text-white/50">
                    Vehicle #{vehicle.id} • VIN {valueOrDash(vehicle.vin)} • {inspections.length} inspection(s) • {photos.length} photo(s)
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ActionButton onClick={() => onSaveVehicle(vehicle.id)} variant="primary" disabled={savingId === `vehicle-${vehicle.id}`}>
                    {savingId === `vehicle-${vehicle.id}` ? "Saving..." : "Save Vehicle"}
                  </ActionButton>
                  <ActionButton onClick={() => onDeleteVehicle(vehicle.id)} variant="danger" disabled={savingId === `delete-vehicle-${vehicle.id}`}>
                    <span className="inline-flex items-center gap-1">
                      <TbTrash className="w-4 h-4" />
                      Delete
                    </span>
                  </ActionButton>
                </div>
              </div>

              {vehicleFieldsGrid(draft, (field, value) => updateVehicle(vehicle.id, field, value), false)}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function InspectionsModal({
  open,
  load,
  inspectionDrafts,
  setInspectionDrafts,
  newInspectionByVehicle,
  setNewInspectionByVehicle,
  onClose,
  onSaveInspection,
  onDeleteInspection,
  onCreateInspection,
  savingId,
}) {
  const updateInspection = (inspectionId, field, value) => {
    setInspectionDrafts((prev) => ({
      ...prev,
      [inspectionId]: {
        ...(prev[inspectionId] || {}),
        [field]: value,
      },
    }));
  };

  const updateNewInspection = (vehicleId, field, value) => {
    setNewInspectionByVehicle((prev) => ({
      ...prev,
      [vehicleId]: {
        ...(prev[vehicleId] || buildInspectionForm({ inspection_type: "Pickup" })),
        [field]: value,
      },
    }));
  };

  const inspectionFieldsGrid = (draft, update, isNew = false) => (
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4">
      <SelectField label="Inspection Type" value={draft.inspection_type} onChange={(v) => update("inspection_type", v)}>
        <option value="">—</option>
        <option value="Pickup">Pickup</option>
        <option value="Delivery">Delivery</option>
      </SelectField>
      <Field label="Odometer" value={draft.odometer} onChange={(v) => update("odometer", v)} />
      <Field label="Customer Name" value={draft.customer_name} onChange={(v) => update("customer_name", v)} />
      <Field label="Customer Phone" value={draft.customer_phone} onChange={(v) => update("customer_phone", v)} />
      <Field label="Customer Email" value={draft.customer_email} onChange={(v) => update("customer_email", v)} />
      <Field label="Keys Quantity" type="number" step="1" inputMode="numeric" value={draft.keys_qt} onChange={(v) => update("keys_qt", v)} />
      <SelectField label="Drivable" value={draft.drivable} onChange={(v) => update("drivable", v)}>
        <option value="">—</option>
        <option value="1">Yes</option>
        <option value="0">No</option>
      </SelectField>
      <SelectField label="Windscreen" value={draft.windscreen} onChange={(v) => update("windscreen", v)}>
        <option value="">—</option>
        <option value="1">Yes</option>
        <option value="0">No</option>
      </SelectField>
      <SelectField label="Intact Glass" value={draft.intact_glass} onChange={(v) => update("intact_glass", v)}>
        <option value="">—</option>
        <option value="1">Yes</option>
        <option value="0">No</option>
      </SelectField>
      <SelectField label="Paperwork" value={draft.paperwork} onChange={(v) => update("paperwork", v)}>
        <option value="">—</option>
        <option value="1">Yes</option>
        <option value="0">No</option>
      </SelectField>
      <SelectField label="Spare Tire" value={draft.spare_tire} onChange={(v) => update("spare_tire", v)}>
        <option value="">—</option>
        <option value="1">Yes</option>
        <option value="0">No</option>
      </SelectField>
      {!isNew && <StaticFileField label="Diagram Path" value={draft.diagram_path} />}
      {!isNew && <StaticFileField label="Customer Signature Path" value={draft.customer_signature_path} />}
      {!isNew && <Field label="Remote Signature Token" value={draft.remote_signature_token} onChange={() => {}} disabled />}
      <div className="md:col-span-3 xl:col-span-2">
        <TextArea label="Inspection Notes" value={draft.notes} onChange={(v) => update("notes", v)} />
      </div>
      <div className="md:col-span-3 xl:col-span-2">
        <TextArea label="Others" value={draft.others} onChange={(v) => update("others", v)} />
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      title="Vehicle Inspections"
      subtitle={load ? `Order ${valueOrDash(load.order_id)} / ${load.id}` : ""}
      icon={<TbShield className="w-5 h-5" />}
      onClose={onClose}
      maxWidth="max-w-7xl"
    >
      <div className="space-y-5">
        {(load?.load_items || load?.loadItems || []).length === 0 && (
          <div className="text-white/60 text-sm border border-dashed border-zinc-700 rounded-xl p-6 text-center">
            No vehicles found for this LoadTransport.
          </div>
        )}

        {(load?.load_items || load?.loadItems || []).map((vehicle) => {
          const inspections = vehicle.inspection_load_items || vehicle.inspectionLoadItems || [];
          const newDraft = newInspectionByVehicle[vehicle.id] || buildInspectionForm({ inspection_type: "Pickup" });

          return (
            <div key={vehicle.id} className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-white font-semibold uppercase">{vehicleLabelUpper(vehicle)}</div>
                  <div className="text-xs text-white/50">Vehicle #{vehicle.id} • {inspections.length} inspection(s)</div>
                </div>
              </div>

              <Section title={`Add inspection for ${vehicleLabel(vehicle)}`} icon={<TbPlus className="w-4 h-4 text-emerald-300" />}>
                <div className="space-y-4">
                  {inspectionFieldsGrid(newDraft, (field, value) => updateNewInspection(vehicle.id, field, value), true)}
                  <div className="flex justify-end">
                    <ActionButton onClick={() => onCreateInspection(vehicle.id)} variant="primary" disabled={savingId === `new-inspection-${vehicle.id}`}>
                      {savingId === `new-inspection-${vehicle.id}` ? "Adding..." : "Add Inspection"}
                    </ActionButton>
                  </div>
                </div>
              </Section>

              {inspections.length === 0 && (
                <div className="text-white/50 text-sm bg-black/20 border border-zinc-700 rounded-xl p-4">
                  No inspections found for this vehicle.
                </div>
              )}

              {inspections.map((inspection) => {
                const draft = inspectionDrafts[inspection.id] || buildInspectionForm(inspection);

                return (
                  <div key={inspection.id} className="bg-black/20 border border-zinc-700 rounded-xl p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
                      <div>
                        <div className="text-white font-semibold">{inspection.inspection_type || "Inspection"} Inspection</div>
                        <div className="text-xs text-white/50">Inspection #{inspection.id} • Odometer {valueOrDash(inspection.odometer)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <ActionButton onClick={() => onSaveInspection(inspection.id)} variant="primary" disabled={savingId === `inspection-${inspection.id}`}>
                          {savingId === `inspection-${inspection.id}` ? "Saving..." : "Save Inspection"}
                        </ActionButton>
                        <ActionButton onClick={() => onDeleteInspection(inspection.id)} variant="danger" disabled={savingId === `delete-inspection-${inspection.id}`}>
                          <span className="inline-flex items-center gap-1">
                            <TbTrash className="w-4 h-4" />
                            Delete
                          </span>
                        </ActionButton>
                      </div>
                    </div>

                    {inspectionFieldsGrid(draft, (field, value) => updateInspection(inspection.id, field, value), false)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function NotesModal({
  open,
  load,
  newNote,
  setNewNote,
  onClose,
  onCreateNote,
  savingId,
}) {
  const notes = load?.notes || [];

  return (
    <Modal
      open={open}
      title="Notes"
      subtitle={load ? `Order ${valueOrDash(load.order_id)} / ${load.id}` : ""}
      icon={<TbNotes className="w-5 h-5" />}
      onClose={onClose}
      maxWidth="max-w-5xl"
    >
      <div className="space-y-5">
        <Section title="Add Note" icon={<TbPlus className="w-4 h-4 text-emerald-300" />}>
          <div className="space-y-3">
            <TextArea label="Message" value={newNote} onChange={setNewNote} rows={4} />
            <div className="flex justify-end">
              <ActionButton onClick={onCreateNote} variant="primary" disabled={savingId === "new-note"}>
                {savingId === "new-note" ? "Adding..." : "Add Note"}
              </ActionButton>
            </div>
          </div>
        </Section>

        {notes.length === 0 && (
          <div className="text-white/60 text-sm border border-dashed border-zinc-700 rounded-xl p-6 text-center">
            No notes found for this LoadTransport.
          </div>
        )}

        <div className="space-y-3">
          {notes.map((note) => {
            const author = note.author ? userName(note.author) : "—";

            return (
              <div key={note.id} className="bg-black/20 border border-zinc-700 rounded-xl p-4">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-3">
                  <div>
                    <div className="text-white font-semibold">Note #{note.id}</div>
                    <div className="text-xs text-white/50">By {author} • {formatDateTime(note.created_at)}</div>
                  </div>
                  <span className="text-[11px] px-2 py-1 rounded-full bg-zinc-800 text-white/50 border border-zinc-700">Read only</span>
                </div>
                <p className="text-sm text-white/75 whitespace-pre-wrap leading-relaxed">{note.message || "—"}</p>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

function DetailCard({ title, icon, children }) {
  return (
    <div className="bg-black/20 border border-zinc-700 rounded-xl p-4 min-h-[170px]">
      <div className="text-white font-semibold mb-3 flex items-center gap-2">
        {icon}
        {title}
      </div>
      <div className="space-y-2 text-xs text-white/70">{children}</div>
    </div>
  );
}

function DetailsModal({ open, load, onClose }) {
  const vehicles = load?.load_items || load?.loadItems || [];
  const timeline = getAuditTimeline(load);
  const pickup = locationLine(load, "pickup");
  const delivery = locationLine(load, "delivery");

  return (
    <Modal
      open={open}
      title="Load Details"
      subtitle={load ? `Order ${valueOrDash(load.order_id)} / ${load.id}` : ""}
      icon={<TbEye className="w-5 h-5" />}
      onClose={onClose}
      maxWidth="max-w-7xl"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 text-sm">
          <DetailCard title="People" icon={<TbUser className="w-4 h-4 text-amber-300" />}>
            <div><span className="text-white/40">Company:</span> {load?.partner_company?.partner_company_name || load?.partnerCompany?.partner_company_name || "—"}</div>
            <div><span className="text-white/40">Shipper:</span> {load?.shipper?.shipper_name || "—"}</div>
            <div><span className="text-white/40">Dispatcher:</span> {userName(load?.dispatcher)}</div>
            <div><span className="text-white/40">Driver:</span> {driverName(load?.driver)}</div>
            <div><span className="text-white/40">Customer User:</span> {userName(load?.customer)}</div>
          </DetailCard>

          <DetailCard title="Pickup" icon={<TbRoute className="w-4 h-4 text-blue-300" />}>
            <div>{pickup || "—"}</div>
            <div><span className="text-white/40">Contact:</span> {valueOrDash(load?.pickup_customer_name)} • {valueOrDash(load?.pickup_customer_phone)}</div>
            <div><span className="text-white/40">Email:</span> {valueOrDash(load?.pickup_customer_email)}</div>
            <div><span className="text-white/40">Expected:</span> {formatDateTime(load?.expected_pickup_date)}</div>
            <div><span className="text-white/40">Actual:</span> {formatDateTime(load?.pickup_date)}</div>
          </DetailCard>

          <DetailCard title="Delivery" icon={<TbRoute className="w-4 h-4 text-emerald-300" />}>
            <div>{delivery || "—"}</div>
            <div><span className="text-white/40">Contact:</span> {valueOrDash(load?.delivery_customer_name)} • {valueOrDash(load?.delivery_customer_phone)}</div>
            <div><span className="text-white/40">Email:</span> {valueOrDash(load?.delivery_customer_email)}</div>
            <div><span className="text-white/40">Expected:</span> {formatDateTime(load?.expected_dropoff_date)}</div>
            <div><span className="text-white/40">Actual:</span> {formatDateTime(load?.dropoff_date)}</div>
          </DetailCard>

          <DetailCard title="Summary" icon={<TbFileText className="w-4 h-4 text-purple-300" />}>
            <div><span className="text-white/40">Status:</span> {load?.load_status?.load_status_name || load?.loadStatus?.load_status_name || "—"}</div>
            <div><span className="text-white/40">Loadboard:</span> {valueOrDash(load?.loadboard)}</div>
            <div><span className="text-white/40">Payment:</span> {money(load?.payment_rate)} • {valueOrDash(load?.payment_type)} • {valueOrDash(load?.payment_method)}</div>
            <div><span className="text-white/40">Vehicles:</span> {vehicles.length}</div>
            <div><span className="text-white/40">Updated:</span> {formatDateTime(load?.updated_at)}</div>
          </DetailCard>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="bg-black/20 border border-zinc-700 rounded-xl p-4 xl:col-span-1">
            <div className="text-white font-semibold mb-3 flex items-center gap-2">
              <TbCar className="w-4 h-4 text-amber-300" />
              Vehicles
            </div>
            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
              {vehicles.length === 0 && <div className="text-sm text-white/50">No vehicles.</div>}
              {vehicles.map((vehicle) => (
                <div key={vehicle.id} className="border border-zinc-700 rounded-lg p-3 bg-zinc-950/30">
                  <div className="text-white text-sm font-semibold uppercase">{vehicleLabelUpper(vehicle)}</div>
                  <div className="text-xs text-white/50 mt-1">VIN {valueOrDash(vehicle.vin)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-black/20 border border-zinc-700 rounded-xl p-4 xl:col-span-2">
            <div className="text-white font-semibold mb-3 flex items-center gap-2">
              <TbFileText className="w-4 h-4 text-purple-300" />
              Audit Context
            </div>
            <div className="max-h-[320px] overflow-y-auto pr-2 space-y-3">
              {timeline.length === 0 && <div className="text-sm text-white/50">No notes or activity logs found.</div>}
              {timeline.map((item) => (
                <div key={item.id} className="border border-zinc-700 rounded-lg p-3 bg-zinc-950/30">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${item.type === "Note" ? "bg-blue-500/10 text-blue-300 border-blue-500/30" : "bg-amber-500/10 text-amber-300 border-amber-500/30"}`}>{item.type}</span>
                        <span className="text-sm text-white font-semibold truncate">{item.title}</span>
                      </div>
                      <p className="text-xs text-white/70 mt-2 whitespace-pre-wrap leading-relaxed">{item.description}</p>
                    </div>
                    <div className="text-right text-[11px] text-white/40 whitespace-nowrap">
                      <div>{formatDateTime(item.date)}</div>
                      <div>{item.author}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default function LoadTransportMaintenance() {
  const [loads, setLoads] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [rangeFrom, setRangeFrom] = useState(0);
  const [rangeTo, setRangeTo] = useState(0);
  const [perPage, setPerPage] = useState(20);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const [filterOptions, setFilterOptions] = useState({
    loadStatuses: [],
    companies: [],
    loadboards: [],
    paymentTypes: [],
    paymentMethods: [],
    locationTypes: [],
    carTypes: [],
    shippers: [],
    drivers: [],
    dispatchers: [],
    customers: [],
    origins: [],
    destinations: [],
  });

  const [tenantOptions, setTenantOptions] = useState({
    shippers: [],
    drivers: [],
    dispatchers: [],
    customers: [],
  });

  const [kpis, setKpis] = useState({
    totalLoads: 0,
    totalCompanies: 0,
    totalVehicles: 0,
    totalPaymentRate: 0,
    lastCreatedAt: null,
  });

  const [selectedLoad, setSelectedLoad] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [vehiclesModalOpen, setVehiclesModalOpen] = useState(false);
  const [inspectionsModalOpen, setInspectionsModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  const [loadForm, setLoadForm] = useState({});
  const [vehicleDrafts, setVehicleDrafts] = useState({});
  const [newVehicle, setNewVehicle] = useState(buildVehicleForm({ is_operable: true }));
  const [inspectionDrafts, setInspectionDrafts] = useState({});
  const [newInspectionByVehicle, setNewInspectionByVehicle] = useState({});
  const [newNote, setNewNote] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const token = () => localStorage.getItem("token");

  const headers = () => ({
    "Screen-Name": "/LoadTransportMaintenance",
    Authorization: `Bearer ${token()}`,
  });

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ open: true, message, type });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 450);

    return () => clearTimeout(timer);
  }, [filters]);

  const updateFilter = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  };

  const parsePaginator = (apiPayload) => {
    if (apiPayload?.data && typeof apiPayload.data === "object" && Array.isArray(apiPayload.data.data)) {
      return apiPayload.data;
    }

    return null;
  };

  const buildParams = (page) => {
    const params = {
      page: page || 1,
      per_page: perPage,
      sort_by: sortBy,
      sort_direction: sortDirection,
      with_kpis: 1,
    };

    Object.entries(debouncedFilters).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "" || value === "all") return;
      params[key] = value;
    });

    return params;
  };

  const fetchOptions = async () => {
    setLoadingOptions(true);

    try {
      const response = await axios.get(`${API_ORIGIN}/api/loadtransport-maintenance/filter-options`, {
        headers: headers(),
      });

      const data = extractData(response) || {};

      setFilterOptions({
        loadStatuses: Array.isArray(data.loadStatuses) ? data.loadStatuses : [],
        companies: Array.isArray(data.companies) ? data.companies : [],
        loadboards: Array.isArray(data.loadboards) ? data.loadboards : [],
        paymentTypes: Array.isArray(data.paymentTypes) ? data.paymentTypes : [],
        paymentMethods: Array.isArray(data.paymentMethods) ? data.paymentMethods : [],
        locationTypes: Array.isArray(data.locationTypes) ? data.locationTypes : [],
        carTypes: Array.isArray(data.carTypes) ? data.carTypes : [],
        shippers: Array.isArray(data.shippers) ? data.shippers : [],
        drivers: Array.isArray(data.drivers) ? data.drivers : [],
        dispatchers: Array.isArray(data.dispatchers) ? data.dispatchers : [],
        customers: Array.isArray(data.customers) ? data.customers : [],
        origins: Array.isArray(data.origins) ? data.origins : [],
        destinations: Array.isArray(data.destinations) ? data.destinations : [],
      });
    } catch (error) {
      showSnackbar("Erro ao carregar opções de filtro", "error");
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchTenantOptions = async (companyId) => {
    if (!companyId) {
      setTenantOptions({ shippers: [], drivers: [], dispatchers: [], customers: [] });
      return;
    }

    try {
      const response = await axios.get(`${API_ORIGIN}/api/loadtransport-maintenance/filter-options`, {
        headers: headers(),
        params: { company_id: companyId },
      });

      const data = extractData(response) || {};
      setTenantOptions({
        shippers: Array.isArray(data.shippers) ? data.shippers : [],
        drivers: Array.isArray(data.drivers) ? data.drivers : [],
        dispatchers: Array.isArray(data.dispatchers) ? data.dispatchers : [],
        customers: Array.isArray(data.customers) ? data.customers : [],
      });
    } catch (error) {
      setTenantOptions({ shippers: [], drivers: [], dispatchers: [], customers: [] });
      showSnackbar("Erro ao carregar opções tenant-safe do Load", "error");
    }
  };

  const fetchLoads = async (page = currentPage) => {
    setLoading(true);

    try {
      const response = await axios.get(`${API_ORIGIN}/api/loadtransport-maintenance/paginated`, {
        headers: headers(),
        params: buildParams(page),
      });

      const paginator = parsePaginator(response.data);
      const list = Array.isArray(paginator?.data) ? paginator.data : [];

      setLoads(list);
      setCurrentPage(Number(paginator?.current_page || page) || 1);
      setTotalPages(Number(paginator?.last_page || 1) || 1);
      setTotalItems(Number(paginator?.total || list.length) || 0);
      setRangeFrom(Number(paginator?.from || (list.length ? 1 : 0)) || 0);
      setRangeTo(Number(paginator?.to || list.length) || 0);
      setPerPage(Number(paginator?.per_page || perPage) || perPage);

      if (paginator?.kpis) {
        setKpis({
          totalLoads: Number(paginator.kpis.totalLoads || 0),
          totalCompanies: Number(paginator.kpis.totalCompanies || 0),
          totalVehicles: Number(paginator.kpis.totalVehicles || 0),
          totalPaymentRate: Number(paginator.kpis.totalPaymentRate || 0),
          lastCreatedAt: paginator.kpis.lastCreatedAt || null,
        });
      }
    } catch (error) {
      setLoads([]);
      setTotalPages(1);
      setTotalItems(0);
      setRangeFrom(0);
      setRangeTo(0);

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Erro ao carregar LoadTransports de maintenance";
      showSnackbar(message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLoads(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, sortBy, sortDirection, debouncedFilters]);

  const refreshSingleLoad = async (loadId) => {
    const response = await axios.get(`${API_ORIGIN}/api/loadtransport-maintenance/${loadId}`, {
      headers: headers(),
    });

    return extractData(response);
  };

  const replaceLoadInState = (updatedLoad) => {
    if (!updatedLoad?.id) return;

    setSelectedLoad(updatedLoad);
    setLoads((prev) => prev.map((item) => (Number(item.id) === Number(updatedLoad.id) ? updatedLoad : item)));
  };

  const openDetailsModal = async (load) => {
    try {
      setSavingId("open-details");
      const fullLoad = await refreshSingleLoad(load.id);
      setSelectedLoad(fullLoad);
      setDetailsModalOpen(true);
    } catch (error) {
      showSnackbar("Erro ao abrir detalhes do LoadTransport", "error");
    } finally {
      setSavingId(null);
    }
  };

  const openLoadModal = async (load) => {
    try {
      setSavingId("open-load");
      const fullLoad = await refreshSingleLoad(load.id);
      setSelectedLoad(fullLoad);
      await fetchTenantOptions(fullLoad.partner_company_id);
      setLoadForm(buildLoadForm(fullLoad));
      setLoadModalOpen(true);
    } catch (error) {
      showSnackbar("Erro ao abrir LoadTransport", "error");
    } finally {
      setSavingId(null);
    }
  };

  const openVehiclesModal = async (load) => {
    try {
      setSavingId("open-vehicles");
      const fullLoad = await refreshSingleLoad(load.id);
      setSelectedLoad(fullLoad);

      const drafts = {};
      (fullLoad.load_items || fullLoad.loadItems || []).forEach((vehicle) => {
        drafts[vehicle.id] = buildVehicleForm(vehicle);
      });

      setVehicleDrafts(drafts);
      setNewVehicle(buildVehicleForm({ is_operable: true }));
      setVehiclesModalOpen(true);
    } catch (error) {
      showSnackbar("Erro ao abrir veículos", "error");
    } finally {
      setSavingId(null);
    }
  };

  const openInspectionsModal = async (load) => {
    try {
      setSavingId("open-inspections");
      const fullLoad = await refreshSingleLoad(load.id);
      setSelectedLoad(fullLoad);

      const drafts = {};
      const newDrafts = {};
      (fullLoad.load_items || fullLoad.loadItems || []).forEach((vehicle) => {
        newDrafts[vehicle.id] = buildInspectionForm({ inspection_type: "Pickup" });
        (vehicle.inspection_load_items || vehicle.inspectionLoadItems || []).forEach((inspection) => {
          drafts[inspection.id] = buildInspectionForm(inspection);
        });
      });

      setInspectionDrafts(drafts);
      setNewInspectionByVehicle(newDrafts);
      setInspectionsModalOpen(true);
    } catch (error) {
      showSnackbar("Erro ao abrir inspeções", "error");
    } finally {
      setSavingId(null);
    }
  };

  const openNotesModal = async (load) => {
    try {
      setSavingId("open-notes");
      const fullLoad = await refreshSingleLoad(load.id);
      setSelectedLoad(fullLoad);

      setNewNote("");
      setNotesModalOpen(true);
    } catch (error) {
      showSnackbar("Erro ao abrir notes", "error");
    } finally {
      setSavingId(null);
    }
  };

  const saveLoadTransport = async () => {
    if (!selectedLoad?.id) return;

    setSavingId("save-load");

    try {
      const response = await axios.post(
        `${API_ORIGIN}/api/loadtransport-maintenance/${selectedLoad.id}/load`,
        normalizeLoadPayload(loadForm),
        { headers: headers() }
      );

      const updated = extractData(response);
      replaceLoadInState(updated);
      setLoadForm(buildLoadForm(updated));
      showSnackbar("LoadTransport atualizado com sucesso", "success");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao atualizar LoadTransport";
      showSnackbar(message, "error");
    } finally {
      setSavingId(null);
    }
  };

  const createVehicle = async () => {
    if (!selectedLoad?.id) return;

    setSavingId("new-vehicle");

    try {
      const response = await axios.post(
        `${API_ORIGIN}/api/loadtransport-maintenance/${selectedLoad.id}/vehicles`,
        normalizeVehiclePayload(newVehicle),
        { headers: headers() }
      );

      const updated = extractData(response);
      replaceLoadInState(updated);

      const drafts = {};
      (updated.load_items || updated.loadItems || []).forEach((vehicle) => {
        drafts[vehicle.id] = buildVehicleForm(vehicle);
      });
      setVehicleDrafts(drafts);
      setNewVehicle(buildVehicleForm({ is_operable: true }));
      showSnackbar("Veículo adicionado com sucesso", "success");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao adicionar veículo";
      showSnackbar(message, "error");
    } finally {
      setSavingId(null);
    }
  };

  const saveVehicle = async (vehicleId) => {
    setSavingId(`vehicle-${vehicleId}`);

    try {
      const response = await axios.post(
        `${API_ORIGIN}/api/loadtransport-maintenance/vehicles/${vehicleId}`,
        normalizeVehiclePayload(vehicleDrafts[vehicleId] || {}),
        { headers: headers() }
      );

      const updated = extractData(response);
      replaceLoadInState(updated);

      const drafts = {};
      (updated.load_items || updated.loadItems || []).forEach((vehicle) => {
        drafts[vehicle.id] = buildVehicleForm(vehicle);
      });
      setVehicleDrafts(drafts);
      showSnackbar("Veículo atualizado com sucesso", "success");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao atualizar veículo";
      showSnackbar(message, "error");
    } finally {
      setSavingId(null);
    }
  };

  const deleteVehicle = async (vehicleId) => {
    const confirmed = window.confirm("Delete this vehicle? This action will be audited in the LoadTransport log.");
    if (!confirmed) return;

    setSavingId(`delete-vehicle-${vehicleId}`);

    try {
      const response = await axios.delete(`${API_ORIGIN}/api/loadtransport-maintenance/vehicles/${vehicleId}`, {
        headers: headers(),
      });

      const updated = extractData(response);
      replaceLoadInState(updated);

      const drafts = {};
      (updated.load_items || updated.loadItems || []).forEach((vehicle) => {
        drafts[vehicle.id] = buildVehicleForm(vehicle);
      });
      setVehicleDrafts(drafts);
      showSnackbar("Veículo removido com sucesso", "success");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao remover veículo";
      showSnackbar(message, "error");
    } finally {
      setSavingId(null);
    }
  };

  const createInspection = async (vehicleId) => {
    setSavingId(`new-inspection-${vehicleId}`);

    try {
      const response = await axios.post(
        `${API_ORIGIN}/api/loadtransport-maintenance/vehicles/${vehicleId}/inspections`,
        normalizeInspectionPayload(newInspectionByVehicle[vehicleId] || buildInspectionForm({ inspection_type: "Pickup" })),
        { headers: headers() }
      );

      const updated = extractData(response);
      replaceLoadInState(updated);

      const drafts = {};
      const newDrafts = {};
      (updated.load_items || updated.loadItems || []).forEach((vehicle) => {
        newDrafts[vehicle.id] = buildInspectionForm({ inspection_type: "Pickup" });
        (vehicle.inspection_load_items || vehicle.inspectionLoadItems || []).forEach((inspection) => {
          drafts[inspection.id] = buildInspectionForm(inspection);
        });
      });
      setInspectionDrafts(drafts);
      setNewInspectionByVehicle(newDrafts);
      showSnackbar("Inspeção adicionada com sucesso", "success");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao adicionar inspeção";
      showSnackbar(message, "error");
    } finally {
      setSavingId(null);
    }
  };

  const saveInspection = async (inspectionId) => {
    setSavingId(`inspection-${inspectionId}`);

    try {
      const response = await axios.post(
        `${API_ORIGIN}/api/loadtransport-maintenance/inspections/${inspectionId}`,
        normalizeInspectionPayload(inspectionDrafts[inspectionId] || {}),
        { headers: headers() }
      );

      const updated = extractData(response);
      replaceLoadInState(updated);

      const drafts = {};
      (updated.load_items || updated.loadItems || []).forEach((vehicle) => {
        (vehicle.inspection_load_items || vehicle.inspectionLoadItems || []).forEach((inspection) => {
          drafts[inspection.id] = buildInspectionForm(inspection);
        });
      });
      setInspectionDrafts(drafts);
      showSnackbar("Inspeção atualizada com sucesso", "success");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao atualizar inspeção";
      showSnackbar(message, "error");
    } finally {
      setSavingId(null);
    }
  };

  const deleteInspection = async (inspectionId) => {
    const confirmed = window.confirm("Delete this inspection? This action will be audited in the LoadTransport log.");
    if (!confirmed) return;

    setSavingId(`delete-inspection-${inspectionId}`);

    try {
      const response = await axios.delete(`${API_ORIGIN}/api/loadtransport-maintenance/inspections/${inspectionId}`, {
        headers: headers(),
      });

      const updated = extractData(response);
      replaceLoadInState(updated);

      const drafts = {};
      (updated.load_items || updated.loadItems || []).forEach((vehicle) => {
        (vehicle.inspection_load_items || vehicle.inspectionLoadItems || []).forEach((inspection) => {
          drafts[inspection.id] = buildInspectionForm(inspection);
        });
      });
      setInspectionDrafts(drafts);
      showSnackbar("Inspeção removida com sucesso", "success");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao remover inspeção";
      showSnackbar(message, "error");
    } finally {
      setSavingId(null);
    }
  };

  const createNote = async () => {
    if (!selectedLoad?.id) return;

    setSavingId("new-note");

    try {
      const response = await axios.post(
        `${API_ORIGIN}/api/loadtransport-maintenance/${selectedLoad.id}/notes`,
        { message: newNote },
        { headers: headers() }
      );

      const updated = extractData(response);
      replaceLoadInState(updated);

      setNewNote("");
      showSnackbar("Note adicionada com sucesso", "success");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao adicionar note";
      showSnackbar(message, "error");
    } finally {
      setSavingId(null);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(column);
    setSortDirection("asc");
  };

  const getLoadVehicles = (load) => load?.load_items || load?.loadItems || [];

  const getStatusName = (load) => {
    return load?.load_status?.load_status_name || load?.loadStatus?.load_status_name || "—";
  };

  const getCompanyName = (load) => {
    return load?.partner_company?.partner_company_name || load?.partnerCompany?.partner_company_name || "—";
  };

  const getShipperName = (load) => {
    return load?.shipper?.shipper_name || "—";
  };

  const getDispatcherName = (load) => userName(load?.dispatcher);

  const statusBadgeClass = (load) => {
    const status = String(getStatusName(load)).toLowerCase();

    if (status.includes("delivered") || status.includes("complete")) {
      return "border-emerald-700 bg-emerald-900/20 text-emerald-200";
    }

    if (status.includes("pickup") || status.includes("assigned") || status.includes("dispatch")) {
      return "border-blue-700 bg-blue-900/20 text-blue-200";
    }

    if (status.includes("cancel") || status.includes("hold")) {
      return "border-red-700 bg-red-900/20 text-red-200";
    }

    return "border-amber-700 bg-amber-900/20 text-amber-200";
  };


  const companyFilterOptions = useMemo(() => {
    return (filterOptions.companies || []).map((company) => ({
      value: String(company.id),
      label: company.partner_company_name || `Company #${company.id}`,
      description: [company.dot ? `DOT ${company.dot}` : "", company.email || ""].filter(Boolean).join(" • "),
    }));
  }, [filterOptions.companies]);

  const shipperFilterOptions = useMemo(() => {
    return (filterOptions.shippers || []).map((shipper) => ({
      value: String(shipper.id),
      label: shipper.shipper_name || `Shipper #${shipper.id}`,
    }));
  }, [filterOptions.shippers]);

  const loadboardFilterOptions = useMemo(() => {
    return (filterOptions.loadboards || []).map((loadboard) => ({
      value: String(loadboard),
      label: String(loadboard),
    }));
  }, [filterOptions.loadboards]);

  const dispatcherFilterOptions = useMemo(() => {
    const apiDispatchers = (filterOptions.dispatchers || []).map((dispatcher) => ({
      value: String(dispatcher.id),
      label: userName(dispatcher),
      description: [dispatcher.email || "", dispatcher.phone || ""].filter(Boolean).join(" • "),
    })).filter((option) => option.label && option.label !== "—");

    if (apiDispatchers.length > 0) {
      return apiDispatchers.sort((a, b) => a.label.localeCompare(b.label));
    }

    const map = new Map();

    loads.forEach((load) => {
      const dispatcher = load?.dispatcher;
      const label = userName(dispatcher);
      if (!dispatcher || label === "—") return;

      const value = String(dispatcher.id || label);
      if (!map.has(value)) {
        map.set(value, {
          value,
          label,
          description: [dispatcher.email || "", dispatcher.phone || ""].filter(Boolean).join(" • "),
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [filterOptions.dispatchers, loads]);

  const originFilterOptions = useMemo(() => {
    const apiOrigins = (filterOptions.origins || [])
      .map((origin) => ({
        value: String(origin.value || origin.label || ""),
        label: String(origin.label || origin.value || ""),
      }))
      .filter((option) => option.value && option.label);

    if (apiOrigins.length > 0) {
      return apiOrigins.sort((a, b) => a.label.localeCompare(b.label));
    }

    const map = new Map();

    loads.forEach((load) => {
      const label = [
        load?.pickup_business_name,
        load?.pickup_address,
        [load?.pickup_city, load?.pickup_state, load?.pickup_zipcode].filter(Boolean).join(" "),
      ].filter(Boolean).join(" • ");

      const value = load?.pickup_zipcode || load?.pickup_address || load?.pickup_city || load?.pickup_state || label;
      if (!label || !value) return;

      const key = String(value).toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          value: String(value),
          label,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [filterOptions.origins, loads]);

  const destinationFilterOptions = useMemo(() => {
    const apiDestinations = (filterOptions.destinations || [])
      .map((destination) => ({
        value: String(destination.value || destination.label || ""),
        label: String(destination.label || destination.value || ""),
      }))
      .filter((option) => option.value && option.label);

    if (apiDestinations.length > 0) {
      return apiDestinations.sort((a, b) => a.label.localeCompare(b.label));
    }

    const map = new Map();

    loads.forEach((load) => {
      const label = [
        load?.delivery_business_name,
        load?.delivery_address,
        [load?.delivery_city, load?.delivery_state, load?.delivery_zipcode].filter(Boolean).join(" "),
      ].filter(Boolean).join(" • ");

      const value = load?.delivery_zipcode || load?.delivery_address || load?.delivery_city || load?.delivery_state || label;
      if (!label || !value) return;

      const key = String(value).toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          value: String(value),
          label,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [filterOptions.destinations, loads]);

  const visibleLoadCount = loads.length;

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection
            title="LOAD MAINTENANCE"
            description="SuperAdmin maintenance center to search, audit and edit LoadTransports across all companies."
          />
        </GroupedContainer>

        {/* Filtros */}
        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg sm:text-xl font-bold">Filters</h2>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setFiltersOpen((prev) => !prev)}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
              >
                <TbFilter className="w-4 h-4" />
                Advanced Filters
                {filtersOpen ? <TbChevronUp className="w-4 h-4" /> : <TbChevronDown className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
              >
                <TbX className="w-4 h-4" /> Clear Filters
              </button>
              <button
                type="button"
                onClick={() => fetchLoads(currentPage)}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
              >
                <TbRefresh className="w-4 h-4" /> Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <FilterTextInput
              value={filters.q}
              onChange={(value) => updateFilter("q", value)}
              onClear={() => updateFilter("q", "")}
              placeholder="Universal Search"
              icon={<TbSearch className="text-white text-lg" />}
            />

            <FilterTextInput
              value={filters.order_id}
              onChange={(value) => updateFilter("order_id", value)}
              onClear={() => updateFilter("order_id", "")}
              placeholder="Order ID"
              icon={<TbSearch className="text-white text-lg" />}
            />

            <FilterSelect
              value={filters.status}
              onChange={(value) => updateFilter("status", value)}
              icon={<TbFilter className="text-amber-500 text-lg" />}
              ariaLabel="Status filter"
            >
              <option value="all" className="bg-[#1c1b19]">All Status</option>
              {(filterOptions.loadStatuses || []).map((status) => (
                <option key={status.id} value={status.id} className="bg-[#1c1b19]">
                  {status.load_status_name}
                </option>
              ))}
            </FilterSelect>

            <HybridSearchDropdown
              selectedValue={filters.company_id}
              searchValue={filters.company_q}
              options={companyFilterOptions}
              allLabel="All Companies"
              placeholder="All Companies"
              icon={<TbSearch className="text-white text-lg" />}
              onSelect={(value) => {
                updateFilter("company_id", value);
                updateFilter("company_q", "");
              }}
              onSearchChange={(value) => {
                updateFilter("company_id", "all");
                updateFilter("company_q", value);
              }}
            />
          </div>

          {filtersOpen && (
            <div className="mt-5 border-t border-zinc-600 pt-5">
              <div className="flex items-center gap-2 mb-4 text-sm font-semibold text-white/80">
                <TbFilter className="text-amber-500 text-lg" />
                Advanced Filters
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <HybridSearchDropdown
                  selectedValue={filters.shipper_q || "all"}
                  searchValue={filters.shipper_q}
                  options={shipperFilterOptions}
                  allLabel="All Shippers"
                  placeholder="All Shippers"
                  icon={<TbSearch className="text-white text-lg" />}
                  onSelect={(value) => updateFilter("shipper_q", value === "all" ? "" : value)}
                  onSearchChange={(value) => updateFilter("shipper_q", value)}
                />

                <FilterTextInput
                  value={filters.customer_q}
                  onChange={(value) => updateFilter("customer_q", value)}
                  onClear={() => updateFilter("customer_q", "")}
                  placeholder="Customer name, phone or email"
                  icon={<TbSearch className="text-white text-lg" />}
                />

                <HybridSearchDropdown
                  selectedValue={filters.dispatcher_q || "all"}
                  searchValue={filters.dispatcher_q}
                  options={dispatcherFilterOptions}
                  allLabel="All Dispatchers"
                  placeholder="All Dispatchers"
                  icon={<TbSearch className="text-white text-lg" />}
                  onSelect={(value) => updateFilter("dispatcher_q", value === "all" ? "" : value)}
                  onSearchChange={(value) => updateFilter("dispatcher_q", value)}
                />

                <HybridSearchDropdown
                  selectedValue={filters.loadboard || "all"}
                  searchValue={filters.loadboard === "all" ? "" : filters.loadboard}
                  options={loadboardFilterOptions}
                  allLabel="All Loadboards"
                  placeholder="All Loadboards"
                  icon={<TbSearch className="text-white text-lg" />}
                  onSelect={(value) => updateFilter("loadboard", value)}
                  onSearchChange={(value) => updateFilter("loadboard", value || "all")}
                />

                <HybridSearchDropdown
                  selectedValue={filters.origin_q || "all"}
                  searchValue={filters.origin_q}
                  options={originFilterOptions}
                  allLabel="All Origins"
                  placeholder="All Origins"
                  icon={<TbSearch className="text-white text-lg" />}
                  onSelect={(value) => updateFilter("origin_q", value === "all" ? "" : value)}
                  onSearchChange={(value) => updateFilter("origin_q", value)}
                />

                <HybridSearchDropdown
                  selectedValue={filters.destination_q || "all"}
                  searchValue={filters.destination_q}
                  options={destinationFilterOptions}
                  allLabel="All Destinations"
                  placeholder="All Destinations"
                  icon={<TbSearch className="text-white text-lg" />}
                  onSelect={(value) => updateFilter("destination_q", value === "all" ? "" : value)}
                  onSearchChange={(value) => updateFilter("destination_q", value)}
                />

                <FilterTextInput
                  type="number"
                  value={filters.payment_rate_min}
                  onChange={(value) => updateFilter("payment_rate_min", value)}
                  onClear={() => updateFilter("payment_rate_min", "")}
                  placeholder="Payment Rate Min"
                  icon={<TbFileText className="text-white text-lg" />}
                />

                <FilterTextInput
                  type="number"
                  value={filters.payment_rate_max}
                  onChange={(value) => updateFilter("payment_rate_max", value)}
                  onClear={() => updateFilter("payment_rate_max", "")}
                  placeholder="Payment Rate Max"
                  icon={<TbFileText className="text-white text-lg" />}
                />

                <FilterSelect
                  value={filters.payment_type}
                  onChange={(value) => updateFilter("payment_type", value)}
                  icon={<TbFilter className="text-amber-500 text-lg" />}
                  ariaLabel="Payment type filter"
                >
                  <option value="all" className="bg-[#1c1b19]">All Payment Types</option>
                  {(filterOptions.paymentTypes || []).map((value) => (
                    <option key={value} value={value} className="bg-[#1c1b19]">{value}</option>
                  ))}
                </FilterSelect>

                <FilterSelect
                  value={filters.payment_method}
                  onChange={(value) => updateFilter("payment_method", value)}
                  icon={<TbFilter className="text-amber-500 text-lg" />}
                  ariaLabel="Payment method filter"
                >
                  <option value="all" className="bg-[#1c1b19]">All Payment Methods</option>
                  {(filterOptions.paymentMethods || []).map((value) => (
                    <option key={value} value={value} className="bg-[#1c1b19]">{value}</option>
                  ))}
                </FilterSelect>
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <DateFilter label="Created From" value={filters.created_from} onChange={(value) => updateFilter("created_from", value)} />
                <DateFilter label="Created To" value={filters.created_to} onChange={(value) => updateFilter("created_to", value)} />
                <DateFilter label="Expected Pickup From" value={filters.expected_pickup_from} onChange={(value) => updateFilter("expected_pickup_from", value)} />
                <DateFilter label="Expected Pickup To" value={filters.expected_pickup_to} onChange={(value) => updateFilter("expected_pickup_to", value)} />
                <DateFilter label="Pickup From" value={filters.pickup_from} onChange={(value) => updateFilter("pickup_from", value)} />
                <DateFilter label="Pickup To" value={filters.pickup_to} onChange={(value) => updateFilter("pickup_to", value)} />
                <DateFilter label="Expected Delivery From" value={filters.expected_delivery_from} onChange={(value) => updateFilter("expected_delivery_from", value)} />
                <DateFilter label="Expected Delivery To" value={filters.expected_delivery_to} onChange={(value) => updateFilter("expected_delivery_to", value)} />
                <DateFilter label="Delivery From" value={filters.delivery_from} onChange={(value) => updateFilter("delivery_from", value)} />
                <DateFilter label="Delivery To" value={filters.delivery_to} onChange={(value) => updateFilter("delivery_to", value)} />
              </div>
            </div>
          )}
        </div>

        {/* Totals cards (match PDF KPIs) */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mt-4">
          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wide">
              <TbTruck className="w-4 h-4" />
              Loads
            </div>
            <div className="text-2xl font-bold text-white mt-2">{kpis.totalLoads}</div>
          </div>
          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wide">
              <TbBuilding className="w-4 h-4" />
              Companies
            </div>
            <div className="text-2xl font-bold text-white mt-2">{kpis.totalCompanies}</div>
          </div>
          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wide">
              <TbCar className="w-4 h-4" />
              Vehicles
            </div>
            <div className="text-2xl font-bold text-white mt-2">{kpis.totalVehicles}</div>
          </div>
          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wide">
              <TbFileText className="w-4 h-4" />
              Payment Rate
            </div>
            <div className="text-2xl font-bold text-white mt-2">{money(kpis.totalPaymentRate)}</div>
          </div>
          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wide">
              <TbCalendar className="w-4 h-4" />
              Last Created
            </div>
            <div className="text-sm font-semibold text-white mt-3">{formatDateTime(kpis.lastCreatedAt)}</div>
          </div>
        </div>
        

        
        <LoadingOverlay show={loading || loadingOptions || Boolean(savingId && String(savingId).startsWith("open-"))} />
        

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-4 mb-2">
          <div className="text-sm text-white/60">
            Showing <span className="text-white font-semibold">{rangeFrom}</span> to <span className="text-white font-semibold">{rangeTo}</span> of <span className="text-white font-semibold">{totalItems}</span> LoadTransports
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-white/50">Per page</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-[#141311] border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto border border-zinc-600 rounded-xl">
          <table className="w-full min-w-[1050px] table-fixed divide-y divide-zinc-600">
            <colgroup>
              <col className="w-[130px]" />
              <col className="w-[160px]" />
              <col className="w-[190px]" />
              <col className="w-[110px]" />
              <col className="w-[130px]" />
              <col className="w-[160px]" />
              <col className="w-[170px]" />
            </colgroup>

            <thead className="bg-[#1c1b19]">
              <tr>
                <th className="px-3 py-2.5 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  <SortButton label="ORDER" column="order_id" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                </th>
                <th className="px-3 py-2.5 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  <SortButton label="COMPANY" column="partner_company_name" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                </th>
                <th className="px-3 py-2.5 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  <SortButton label="ROUTE" column="pickup_city" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                </th>
                <th className="px-3 py-2.5 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  <SortButton label="PAYMENT" column="payment_rate" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                </th>
                <th className="px-3 py-2.5 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  <SortButton label="CREATED" column="created_at" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                </th>
                <th className="px-3 py-2.5 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">VEHICLES</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">ACTIONS</th>
              </tr>
            </thead>

            <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
              {visibleLoadCount === 0 && !loading && (
                <tr>
                  <td colSpan="7" className="px-3 py-8 text-center text-gray-400">
                    No LoadTransports found matching your filters.
                  </td>
                </tr>
              )}

              {loads.map((load) => {
                const vehicles = getLoadVehicles(load);
                const pickup = [load.pickup_city, load.pickup_state, load.pickup_zipcode].filter(Boolean).join(", ");
                const delivery = [load.delivery_city, load.delivery_state, load.delivery_zipcode].filter(Boolean).join(", ");
                const statusName = getStatusName(load);
                const assignedDriverName = driverName(load.driver);
                const hasDriver = assignedDriverName && assignedDriverName !== "—";

                return (
                  <tr key={load.id} className="hover:bg-zinc-800/30 transition-colors align-top">
                    <td className="px-3 py-3 text-sm font-medium text-white align-top">
                      <div className="max-w-[15ch] whitespace-normal break-words leading-snug" title={`${valueOrDash(load.order_id)}/${load.id}`}>
                        <span>{valueOrDash(load.order_id)}</span>
                        <span className="text-gray-500">/{load.id}</span>
                      </div>

                      <div className="mt-1.5">
                        <span className={`inline-flex max-w-[15ch] items-center rounded-full border px-2 py-0.5 text-[11px] leading-tight whitespace-normal break-words ${statusBadgeClass(load)}`} title={statusName}>
                          {statusName}
                        </span>
                      </div>

                      <div className="mt-1 max-w-[15ch] whitespace-normal break-words text-[11px] leading-tight text-gray-500" title={valueOrDash(load.loadboard)}>
                        {valueOrDash(load.loadboard)}
                      </div>
                    </td>

                    <td className="px-3 py-3 text-sm text-gray-300 align-top">
                      <div className="max-w-[20ch] whitespace-normal break-words font-medium leading-snug text-white" title={getCompanyName(load)}>{getCompanyName(load)}</div>
                      <div className="mt-1 max-w-[20ch] whitespace-normal break-words text-[11px] leading-tight text-gray-500" title={getShipperName(load)}>Shipper: {getShipperName(load)}</div>
                    </td>

                    <td className="px-3 py-3 text-sm text-gray-300 align-top">
                      <div className="max-w-[20ch] space-y-1">
                        <div className="flex items-start gap-2 text-left text-xs text-gray-300">
                          <span className="mt-1 flex h-2.5 w-2.5 flex-shrink-0 rounded-full bg-green-500 ring-2 ring-green-200"></span>
                          <p className="max-w-[20ch] whitespace-normal break-words font-medium leading-snug text-white" title={pickup || "—"}>{pickup || "—"}</p>
                        </div>
                        
                        <div className="flex items-start gap-2 text-left text-xs text-gray-400">
                          <span className="mt-1 flex h-2.5 w-2.5 flex-shrink-0 rounded-full bg-red-500 ring-2 ring-red-200"></span>
                          <p className="max-w-[20ch] whitespace-normal break-words font-medium leading-snug text-white" title={delivery || "—"}>{delivery || "—"}</p>
                        </div>

                        <div className="flex items-start gap-1 pt-1 text-[11px] leading-tight text-gray-400">
                          <TbTruck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                          <span className="max-w-[20ch] whitespace-normal break-words" title={hasDriver ? assignedDriverName : "No driver assigned"}>
                            {hasDriver ? assignedDriverName : "No driver assigned"}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-3 text-sm font-medium text-white align-top">
                      <div className="whitespace-nowrap">{money(load.payment_rate)}</div>
                      <div className="mt-1 text-[11px] leading-tight text-gray-500">{valueOrDash(load.payment_type)} • {valueOrDash(load.payment_method)}</div>
                    </td>

                    <td className="px-3 py-3 text-xs leading-tight text-gray-300 align-top">{formatDateTime(load.created_at)}</td>

                    <td className="px-3 py-3 text-sm text-gray-300 align-top">
                      <div className="max-w-[20ch] space-y-1">
                        {vehicles.length === 0 && <div className="text-xs text-gray-500">No vehicles</div>}
                        {vehicles.map((vehicle) => (
                          <div key={vehicle.id} className="max-w-[20ch] whitespace-normal break-words text-xs font-medium uppercase leading-snug text-gray-200" title={vehicleLabelUpper(vehicle)}>
                            {vehicleLabelUpper(vehicle)}
                          </div>
                        ))}
                      </div>
                    </td>

                    <td className="px-3 py-3 whitespace-nowrap align-top">
                      <div className="flex items-center gap-1">
                        <IconOnlyButton title="Load Details" onClick={() => openDetailsModal(load)} tone="info" disabled={savingId === "open-details"}>
                          <TbEye className="w-4 h-4" />
                        </IconOnlyButton>
                        <IconOnlyButton title="Load Edit" onClick={() => openLoadModal(load)} disabled={savingId === "open-load"}>
                          <TbEdit className="w-4 h-4" />
                        </IconOnlyButton>
                        <IconOnlyButton title="Vehicles Add/Edit" onClick={() => openVehiclesModal(load)} tone="success" disabled={savingId === "open-vehicles"}>
                          <TbCar className="w-4 h-4" />
                        </IconOnlyButton>
                        <IconOnlyButton title="Inspections Add/Edit" onClick={() => openInspectionsModal(load)} tone="default" disabled={savingId === "open-inspections"}>
                          <TbShield className="w-4 h-4" />
                        </IconOnlyButton>
                        <IconOnlyButton title="Notes Add" onClick={() => openNotesModal(load)} tone="default" disabled={savingId === "open-notes"}>
                          <TbNotes className="w-4 h-4" />
                        </IconOnlyButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-4">
          <div className="text-sm text-white/50">
            Page {currentPage} of {totalPages}
          </div>

          <div className="flex items-center gap-2">
            <ActionButton
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage <= 1 || loading}
              variant="ghost"
            >
              <span className="inline-flex items-center gap-1">
                <ChevronLeft className="w-4 h-4" />
                Previous
              </span>
            </ActionButton>

            <ActionButton
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages || loading}
              variant="ghost"
            >
              <span className="inline-flex items-center gap-1">
                Next
                <ChevronRight className="w-4 h-4" />
              </span>
            </ActionButton>
          </div>
        </div>
        
        
      </div>

      <DetailsModal
        open={detailsModalOpen}
        load={selectedLoad}
        onClose={() => setDetailsModalOpen(false)}
      />

      <LoadEditModal
        open={loadModalOpen}
        load={selectedLoad}
        form={loadForm}
        setForm={setLoadForm}
        filterOptions={filterOptions}
        tenantOptions={tenantOptions}
        onClose={() => setLoadModalOpen(false)}
        onSave={saveLoadTransport}
        saving={savingId === "save-load"}
      />

      <VehiclesModal
        open={vehiclesModalOpen}
        load={selectedLoad}
        carTypes={filterOptions.carTypes}
        vehicleDrafts={vehicleDrafts}
        setVehicleDrafts={setVehicleDrafts}
        newVehicle={newVehicle}
        setNewVehicle={setNewVehicle}
        onClose={() => setVehiclesModalOpen(false)}
        onSaveVehicle={saveVehicle}
        onDeleteVehicle={deleteVehicle}
        onCreateVehicle={createVehicle}
        savingId={savingId}
      />

      <InspectionsModal
        open={inspectionsModalOpen}
        load={selectedLoad}
        inspectionDrafts={inspectionDrafts}
        setInspectionDrafts={setInspectionDrafts}
        newInspectionByVehicle={newInspectionByVehicle}
        setNewInspectionByVehicle={setNewInspectionByVehicle}
        onClose={() => setInspectionsModalOpen(false)}
        onSaveInspection={saveInspection}
        onDeleteInspection={deleteInspection}
        onCreateInspection={createInspection}
        savingId={savingId}
      />

      <NotesModal
        open={notesModalOpen}
        load={selectedLoad}
        newNote={newNote}
        setNewNote={setNewNote}
        onClose={() => setNotesModalOpen(false)}
        onCreateNote={createNote}
        savingId={savingId}
      />

      {snackbar.open && (
        <Snackbar message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} duration={5000} />
      )}
    </AuthenticatedLayout>
  );
}